/**
 * Utility functions for time and duration calculations
 */

/**
 * Calculate flight duration accurately using explicit timezone conversion
 * 
 * @param {string|Date} departureTime - Departure time as ISO string or Date object
 * @param {string|Date} arrivalTime - Arrival time as ISO string or Date object
 * @param {string} departureTimezone - Departure timezone (e.g., 'Asia/Tokyo' or offset like '+09:00')
 * @param {string} arrivalTimezone - Arrival timezone (e.g., 'Europe/Paris' or offset like '+02:00')
 * @returns {Object} Duration object with hours, minutes, and formatted string
 */
export const calculateFlightDuration = (departureTime, arrivalTime, departureTimezone, arrivalTimezone) => {
  if (!departureTime || !arrivalTime) {
    return { 
      hours: 0, 
      minutes: 0, 
      formatted: null
    };
  }
    
  try {
    // Manually handle timezone conversions to get accurate duration

    // 1. Parse departure and arrival times
    const departure = departureTime instanceof Date ? departureTime : new Date(departureTime);
    const arrival = arrivalTime instanceof Date ? arrivalTime : new Date(arrivalTime);

    // Validate that dates are valid
    if (isNaN(departure.getTime()) || isNaN(arrival.getTime())) {
      throw new Error('Invalid date string provided');
    }

    // 2. Get timezone offsets (in minutes)
    // For Tokyo (JST): UTC+9 = 9 * 60 = 540 minutes
    // For Paris (CEST): UTC+2 = 2 * 60 = 120 minutes
    const departureOffset = getTimezoneOffsetMinutes(departureTimezone);
    const arrivalOffset = getTimezoneOffsetMinutes(arrivalTimezone);
        
    // 3. Convert to UTC by adjusting for timezone differences
    // Note: This assumes departureTime and arrivalTime are stored in their local timezones
    // Example:
    // Tokyo 12:35 PM JST = 03:35 AM UTC (subtract 9 hours or 540 minutes)
    // Paris 7:45 AM CEST = 05:45 AM UTC (subtract 2 hours or 120 minutes)
        
    // Create new Date objects with adjusted times for UTC
    const departureUTC = new Date(
      departure.getTime() - (departureOffset * 60 * 1000)
    );
        
    const arrivalUTC = new Date(
      arrival.getTime() - (arrivalOffset * 60 * 1000)
    );
        
    // 4. Calculate duration in milliseconds between UTC times
    const durationMs = arrivalUTC.getTime() - departureUTC.getTime();
        
    if (durationMs < 0) {
      console.warn('Negative duration calculated, may indicate incorrect date/time inputs');
      return { 
        hours: 0, 
        minutes: 0, 
        formatted: null
      };
    }
        
    // 5. Convert to hours and minutes
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
        
    return {
      hours,
      minutes,
      formatted: `${hours}h ${minutes}m`
    };
  } catch (e) {
    console.error('Error calculating flight duration:', e);
    return { 
      hours: 0, 
      minutes: 0, 
      formatted: null
    };
  }
};

/**
 * Extract timezone offset in minutes from a timezone string
 * 
 * @param {string} timezone - Timezone string like 'Asia/Tokyo', 'Europe/Paris', '+09:00', or '+0900'
 * @returns {number} Offset in minutes from UTC
 */
function getTimezoneOffsetMinutes(timezone) {
  if (!timezone) {
    return 0;
  }
    
  try {
    // Handle offset format like '+09:00' or '+0900'
    if (timezone.startsWith('+') || timezone.startsWith('-')) {
      const sign = timezone.startsWith('+') ? 1 : -1;
      const parts = timezone.substring(1).split(':');
            
      if (parts.length === 1 && parts[0].length === 4) {
        // Format: +0900
        const hours = parseInt(parts[0].substring(0, 2), 10);
        const minutes = parseInt(parts[0].substring(2), 10);
        return sign * (hours * 60 + minutes);
      } else if (parts.length === 2) {
        // Format: +09:00
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        return sign * (hours * 60 + minutes);
      }
    }
        
    // Handle common timezone names
    const timezoneMap = {
      'Asia/Tokyo': 9 * 60,        // JST (UTC+9)
      'Japan': 9 * 60,             // JST (UTC+9)
      'JST': 9 * 60,               // Japan Standard Time
      'Europe/Paris': 2 * 60,      // CEST (UTC+2) during summer
      'Europe/Berlin': 2 * 60,     // CEST (UTC+2) during summer
      'CET': 1 * 60,               // Central European Time (winter)
      'CEST': 2 * 60,              // Central European Summer Time (summer)
      'America/New_York': -4 * 60, // EDT (UTC-4) during summer
      'America/Los_Angeles': -7 * 60, // PDT (UTC-7) during summer
      'America/Chicago': -5 * 60,  // CDT (UTC-5) during summer
      'UTC': 0,                    // Universal Time Coordinated
      'GMT': 0                     // Greenwich Mean Time
    };
        
    if (timezoneMap[timezone] !== undefined) {
      return timezoneMap[timezone];
    }
        
    // If we can't determine the offset, return 0 (UTC)
    console.warn(`Couldn't determine timezone offset for "${timezone}", using UTC instead`);
    return 0;
  } catch (e) {
    console.error('Error determining timezone offset:', e);
    return 0;
  }
}

/**
 * Format a duration in milliseconds to a human-readable string
 * @param {number} durationMs - Duration in milliseconds
 * @returns {string} Formatted duration string (e.g. "14h 25m")
 */
export const formatDuration = (durationMs) => {
  // Validate input is a valid number
  if (typeof durationMs !== 'number' || !durationMs || durationMs < 0 || !isFinite(durationMs)) {
    return null;
  }

  try {
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  } catch (e) {
    console.error('Error formatting duration:', e);
    return null;
  }
}; 